import Reports from 'Api/Reports';

export type CombinedReportAiJob = {
    jobId?: string;
    status?: string;
    phase?: string;
    error?: string;
    generateJobId?: string;
    messageId?: string;
    schoolId?: number | string;
    templateId?: number | string;
    reportId?: number | string;
    [key: string]: unknown;
};

export type PollCombinedReportAiOptions = {
    intervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
};

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

/** Allow small clock skew between client enqueue time and server job timestamps. */
const WATCH_TIME_BUFFER_MS = 5000;

function parseJobTimeMs(job: CombinedReportAiJob): number | null {
    const raw = job.finishedAt || job.updatedAt || job.createdAt || job.startedAt;
    if (raw == null) return null;
    const ms = new Date(String(raw)).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/** Ignore terminal rows from an earlier run for the same school/report. */
function isJobFromThisWatch(job: CombinedReportAiJob, watchStartedAt?: number): boolean {
    if (!watchStartedAt) return true;
    const jobTime = parseJobTimeMs(job);
    if (jobTime == null) return false;
    return jobTime >= watchStartedAt - WATCH_TIME_BUFFER_MS;
}

function isLinkedToMessage(job: CombinedReportAiJob, messageId?: string, linkedGenerateId?: string): boolean {
    if (!messageId && !linkedGenerateId) return false;
    const jobId = job?.jobId ? String(job.jobId) : '';
    if (linkedGenerateId && jobId === String(linkedGenerateId)) return true;
    if (messageId) {
        const jobMessageId = String(job.messageId || jobId || '');
        if (jobMessageId === String(messageId)) return true;
    }
    return false;
}

function belongsToThisWatch(
    job: CombinedReportAiJob,
    watchStartedAt: number | undefined,
    messageId?: string,
    linkedGenerateId?: string,
): boolean {
    if (isLinkedToMessage(job, messageId, linkedGenerateId)) return true;
    return isJobFromThisWatch(job, watchStartedAt);
}

function sleep(ms: number, signal?: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const timer = setTimeout(resolve, ms);
        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * Poll GET /api/v2/reports/ai-summary/generate/:jobId until completed/failed or timeout.
 * If the row is an SQS wait-record with generateJobId, follow that worker job.
 */
export async function pollCombinedReportAiJob(
    jobId: string,
    { intervalMs = 3000, timeoutMs = 10 * 60 * 1000, signal }: PollCombinedReportAiOptions = {},
): Promise<CombinedReportAiJob> {
    const started = Date.now();
    let pollId = jobId;
    let pending = true;

    while (pending) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        if (Date.now() - started > timeoutMs) {
            throw new Error('Timed out waiting for AI report summary');
        }

        const data = await Reports.getCombinedReportAiStatus(pollId);
        const job = (data?.job || data) as CombinedReportAiJob;
        const status = String(job?.status || '').toLowerCase();
        const linkedGenerateId = job?.generateJobId ? String(job.generateJobId) : '';

        // SQS wait row linked to a worker job — poll the generate job instead.
        if (linkedGenerateId && linkedGenerateId !== String(pollId) && !TERMINAL.has(status)) {
            pollId = linkedGenerateId;
            await sleep(intervalMs, signal);
            continue;
        }

        if (TERMINAL.has(status)) {
            pending = false;
            return {
                ...job,
                jobId: String(job?.jobId || linkedGenerateId || pollId),
            };
        }

        await sleep(intervalMs, signal);
    }

    throw new Error('Timed out waiting for AI report summary');
}

function matchesQueuedReport(
    job: CombinedReportAiJob,
    schoolId: number,
    reportId: number,
): boolean {
    const jobSchoolId = job?.schoolId;
    const jobReportId = job?.templateId ?? job?.reportId;
    return Number(jobSchoolId) === Number(schoolId) && Number(jobReportId) === Number(reportId);
}

/**
 * Poll GET /api/v2/reports/ai-summary/generate/pending until a matching
 * school/template job is completed/failed (used after SQS enqueue, which has no jobId yet).
 *
 * When `messageId` is provided, also poll that Redis row (and follow generateJobId)
 * so completion is detected even if the user pending set was never populated.
 */
export async function pollPendingCombinedReportAiJob(
    {
        schoolId,
        reportId,
        messageId,
        watchStartedAt,
    }: { schoolId: number; reportId: number; messageId?: string; watchStartedAt?: number },
    { intervalMs = 3000, timeoutMs = 10 * 60 * 1000, signal }: PollCombinedReportAiOptions = {},
): Promise<CombinedReportAiJob> {
    const started = Date.now();
    let pending = true;

    while (pending) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        if (Date.now() - started > timeoutMs) {
            throw new Error('Timed out waiting for AI report summary');
        }

        let linkedGenerateId = '';

        // Prefer following the SQS messageId → generateJobId chain when available.
        if (messageId) {
            try {
                const data = await Reports.getCombinedReportAiStatus(messageId);
                const job = (data?.job || data) as CombinedReportAiJob;
                const status = String(job?.status || '').toLowerCase();
                linkedGenerateId = job?.generateJobId ? String(job.generateJobId) : '';

                if (linkedGenerateId) {
                    try {
                        const generateData = await Reports.getCombinedReportAiStatus(linkedGenerateId);
                        const generateJob = (generateData?.job || generateData) as CombinedReportAiJob;
                        const generateStatus = String(generateJob?.status || '').toLowerCase();
                        if (
                            TERMINAL.has(generateStatus) &&
                            belongsToThisWatch(generateJob, watchStartedAt, messageId, linkedGenerateId)
                        ) {
                            pending = false;
                            return {
                                ...generateJob,
                                jobId: String(generateJob?.jobId || linkedGenerateId),
                                messageId,
                            };
                        }
                    } catch {
                        /* generate row not written yet — keep polling */
                    }
                } else if (
                    TERMINAL.has(status) &&
                    belongsToThisWatch(job, watchStartedAt, messageId)
                ) {
                    pending = false;
                    return {
                        ...job,
                        jobId: String(job?.jobId || messageId),
                        messageId,
                    };
                }
            } catch {
                /* 404 while Lambda has not written Redis yet — fall through to pending list */
            }
        }

        const data = await Reports.getPendingCombinedReportAi();
        const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
        const match = jobs.find(
            (job: CombinedReportAiJob) =>
                matchesQueuedReport(job, schoolId, reportId) &&
                belongsToThisWatch(job, watchStartedAt, messageId, linkedGenerateId || undefined),
        );
        if (match) {
            const status = String(match?.status || '').toLowerCase();
            if (TERMINAL.has(status)) {
                pending = false;
                return match as CombinedReportAiJob;
            }
        }

        await sleep(intervalMs, signal);
    }

    throw new Error('Timed out waiting for AI report summary');
}
